import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import ItemTabBar from "./itemTabBar";

/** Stable JSON stringify (sorts object keys) so comparison is not affected by key order. */
function stableStringify(value) {
  if (value === null || value === undefined) return JSON.stringify(value);
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "string") return JSON.stringify(value);
  if (value instanceof Date) return JSON.stringify(value.getTime());
  if (Array.isArray(value)) return "[" + value.map((item) => stableStringify(item)).join(",") + "]";
  const keys = Object.keys(value).sort();
  const pairs = keys.map((k) => JSON.stringify(k) + ":" + stableStringify(value[k]));
  return "{" + pairs.join(",") + "}";
}

const UpdateItemInventory = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const reduxProfileData = useSelector((state) => state.auth.profileData);
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const toInt = (v) => {
    if (v === null || v === undefined) return 0;
    const n = typeof v === "number" ? v : parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
  };

  const toFloat = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    const n = typeof v === "number" ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  /** Read item attribute supporting both camelCase and PascalCase from API so we preserve previous state. */
  const getItemAttr = (d, camelKey, pascalKey, fallback) =>
    d == null ? fallback : (d[camelKey] ?? d[pascalKey] ?? fallback);

  const getEntriesFromResponse = (response) => {
    const data = response?.data?.data ?? response?.data?.Data ?? response?.data ?? [];
    return Array.isArray(data) ? data : [];
  };

  const sumStockQty = (entries) =>
    (Array.isArray(entries) ? entries : []).reduce(
      (sum, e) => sum + toFloat(e?.quantity ?? e?.Quantity),
      0
    );

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [itemData, setItemData] = useState(null);
  const hasFetchedDetailsRef = useRef(false);
  const [stockEntries, setStockEntries] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);

  // Parent-only: variant inventory overview (Option B)
  const [companyProfile, setCompanyProfile] = useState(null);
  const parentCompId = toInt(
    (companyProfile?.parentComID ?? companyProfile?.ParentComID) ??
    (reduxProfileData?.parentComID ?? reduxProfileData?.ParentComID)
  );
  const isParentCompany = parentCompId <= 0;
  const focusVariantId = toInt(location?.state?.focusVariantId);

  const [assignedBranches, setAssignedBranches] = useState([]); // [{ branchCompId, branchName }]
  const [hasBranches, setHasBranches] = useState(false); // Whether company has any branches
  const [variantItems, setVariantItems] = useState([]); // raw variant list from itemData.products
  const [variantStockMap, setVariantStockMap] = useState({}); // keyed by variantId
  const [loadingVariantOverview, setLoadingVariantOverview] = useState(false);

  // Parent-only: branch stock overview for non-variant items
  const [itemBranchStockMap, setItemBranchStockMap] = useState({}); // keyed by branchCompId
  const [loadingItemBranchTotals, setLoadingItemBranchTotals] = useState(false);

  // Ref to track if variant totals have been loaded (prevent re-loading on every variantItems change)
  const hasLoadedVariantTotalsRef = useRef(false);

  const [inventoryState, setInventoryState] = useState({
    current_Quantity: "",
    reorder_Quantity: "",
    reorder_Level: "",
    availableQuantity: "",
    orderQuantityLimit: "",
    menuItem_IsBuyingProduct: false,
    menuItem_IsStockingProduct: false,
    menuItem_SourceType: "Purchase",
    menuItem_HasRecipe: false,
    menuItem_RecipeId: "",
    menuItem_RecipeType: "Made to order",
    menuItem_IsTrackBatch: false,
    menuItem_IsTrackStock: false,
    menuItem_SellingMethodBoth: false,
    menuItem_PurchaseContainerType: "Individual",
    menuItem_PurchaseContainerQty: 1,
    menuItem_MeasurementType: "",
    menuItem_DefaultSize: "",
    menuItem_DefaultSizeUnit: "",
    menuitem_DefaultSizeInBaseUnit: 0,
  });

  // Track initial state for change detection
  const [initialInventoryState, setInitialInventoryState] = useState(null);
  const [initialVariantItems, setInitialVariantItems] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [baselineReady, setBaselineReady] = useState(false);
  const baselineReadyRef = useRef(false);

  const captureBaselineSnapshot = useCallback(() => {
    setInitialInventoryState(JSON.parse(JSON.stringify(inventoryState)));
    setInitialVariantItems(JSON.parse(JSON.stringify(variantItems)));
    setHasUnsavedChanges(false);
    baselineReadyRef.current = true;
    setBaselineReady(true);
  }, [inventoryState, variantItems]);

  const hasVariants = !!(
    itemData?.menuItem_HasVariant ??
    itemData?.MenuItem_HasVariant ??
    (Array.isArray(itemData?.products) && itemData.products.length > 0)
  );

  const showVariantOverview = isParentCompany && hasVariants && variantItems.length > 0;

  const fetchCompanyProfile = useCallback(async () => {
    try {
      const res = await axiosInstance.get(APIs.GET_COMPANY_PROFILE);
      setCompanyProfile(res?.data?.data || null);
    } catch (e) {
      setCompanyProfile(null);
    }
  }, []);

  const renderStockEntriesTable = (entries, { compact = false } = {}) => {
    const list = Array.isArray(entries) ? entries : [];
    const cellPadding = "8px 12px";
    const tableFontSize = "13px";

    if (list.length === 0) {
      return (
        <div className="text-center py-2" style={{ color: isDarkMode ? "#adb5bd" : "#6c757d", fontSize: tableFontSize }}>
          <i className="fa fa-inbox me-2" style={{ fontSize: 16 }}></i>
          <span>No stock entries found</span>
        </div>
      );
    }

    return (
      <div className="table-responsive">
        <table
          className="table table-hover"
          style={{
            backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
            color: isDarkMode ? "#ffffff" : "#000000",
            marginBottom: compact ? 0 : undefined,
            fontSize: tableFontSize,
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: isDarkMode ? "#3c4043" : "#f8f9fa",
                borderBottom: `2px solid ${themeColor}`,
              }}
            >
              <th style={{ padding: cellPadding, fontWeight: "600" }}>Batch Number</th>
              <th style={{ padding: cellPadding, fontWeight: "600" }}>Source Type</th>
              <th style={{ padding: cellPadding, fontWeight: "600" }}>Quantity</th>
              <th style={{ padding: cellPadding, fontWeight: "600" }}>Unit</th>
              <th style={{ padding: cellPadding, fontWeight: "600" }}>Container Type</th>
              <th style={{ padding: cellPadding, fontWeight: "600" }}>Container Count</th>
              <th style={{ padding: cellPadding, fontWeight: "600" }}>Cost Price</th>
              <th style={{ padding: cellPadding, fontWeight: "600" }}>Receipt Date</th>
              <th style={{ padding: cellPadding, fontWeight: "600" }}>Expiry Date</th>
              <th style={{ padding: cellPadding, fontWeight: "600" }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {list.map((entry, index) => {
              const batchNumber = entry?.batchNumber ?? entry?.BatchNumber ?? "-";
              const sourceType = entry?.sourceType ?? entry?.SourceType ?? "-";
              const qty = entry?.quantity ?? entry?.Quantity ?? 0;
              const unit = entry?.unit ?? entry?.Unit ?? "-";
              const containerType = entry?.containerType ?? entry?.ContainerType ?? null;
              const containerCount = entry?.containerCount ?? entry?.ContainerCount ?? null;
              const containerQuantity = entry?.containerQuantity ?? entry?.ContainerQuantity ?? null;
              const costPrice = entry?.costPrice ?? entry?.CostPrice ?? 0;
              const receiptDate = entry?.receiptDate ?? entry?.ReceiptDate ?? null;
              const expiryDate = entry?.expiryDate ?? entry?.ExpiryDate ?? null;
              const isActive = entry?.isActive ?? entry?.IsActive ?? true;

              return (
                <tr
                  key={entry?.inventoryStockId ?? entry?.InventoryStockId ?? index}
                  style={{
                    borderBottom: isDarkMode ? "1px solid #404040" : "1px solid #e9ecef",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isDarkMode ? "#3c4043" : "#f8f9fa";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isDarkMode ? "#2d3436" : "#ffffff";
                  }}
                >
                  <td style={{ padding: cellPadding }}>{batchNumber}</td>
                  <td style={{ padding: cellPadding }}>
                    <span
                      className="badge"
                      style={{
                        backgroundColor: getSourceTypeColor(sourceType),
                        color: "#ffffff",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                      }}
                    >
                      {sourceType}
                    </span>
                  </td>
                  <td style={{ padding: cellPadding, fontWeight: "500" }}>
                    {toFloat(qty).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td style={{ padding: cellPadding }}>{unit}</td>
                  <td style={{ padding: cellPadding }}>
                    {containerType ? (
                      <span
                        className="badge"
                        style={{
                          backgroundColor: containerType === "Individual" ? "#6c757d" : "#17a2b8",
                          color: "#ffffff",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                        }}
                      >
                        {containerType}
                        {containerType !== "Individual" && containerQuantity != null && containerQuantity > 0 && (
                          <> ({containerQuantity} units)</>
                        )}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td style={{ padding: cellPadding, fontWeight: "500" }}>
                    {(() => {
                      const contQtyNum = toFloat(containerQuantity) || 0;
                      const numContainers = (containerCount != null && containerCount !== undefined)
                        ? toFloat(containerCount)
                        : (contQtyNum > 0 && containerType && containerType !== "Individual")
                          ? toFloat(qty) / contQtyNum
                          : null;
                      if (numContainers == null || numContainers <= 0) return <span>-</span>;
                      return (
                        <span>
                          {numContainers.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                          {containerType && containerType !== "Individual" && (
                            <span style={{ fontSize: "11px", color: isDarkMode ? "#adb5bd" : "#6c757d", marginLeft: "4px" }}>{containerType.toLowerCase()}</span>
                          )}
                          {(!containerType || containerType === "Individual") && (
                            <span style={{ fontSize: "11px", color: isDarkMode ? "#adb5bd" : "#6c757d", marginLeft: "4px" }}>items</span>
                          )}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={{ padding: cellPadding }}>
                    {toFloat(costPrice).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 4,
                    })}
                  </td>
                  <td style={{ padding: cellPadding }}>
                    {receiptDate ? new Date(receiptDate).toLocaleDateString() : "-"}
                  </td>
                  <td style={{ padding: cellPadding }}>
                    {expiryDate ? (
                      <span
                        style={{
                          color: isExpired(expiryDate)
                            ? "#dc3545"
                            : isDarkMode
                              ? "#ffffff"
                              : "#000000",
                          fontWeight: isExpired(expiryDate) ? "600" : "normal",
                        }}
                      >
                        {new Date(expiryDate).toLocaleDateString()}
                        {isExpired(expiryDate) && (
                          <i className="fa fa-exclamation-triangle ms-2" style={{ fontSize: "12px" }}></i>
                        )}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td style={{ padding: cellPadding }}>
                    <span
                      className="badge"
                      style={{
                        backgroundColor: isActive ? "#28a745" : "#dc3545",
                        color: "#ffffff",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                      }}
                    >
                      {isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Fetch item data
  const fetchItemDetails = useCallback(async () => {
    if (!itemId || !CompID) return;

    try {
      baselineReadyRef.current = false;
      setBaselineReady(false);
      setHasUnsavedChanges(false);
      setInitialLoading(true);
      const response = await axiosInstance.get(APIs.GET_ITEM_BY_ID, {
        params: { CompID, MenuItem_ID: itemId },
      });

      const itemData = response.data.data || response.data;

      if (itemData) {
        setItemData(itemData);
        const toStr = (v) => (v === null || v === undefined ? "" : String(v));
        const initialState = {
          current_Quantity: toStr(getItemAttr(itemData, "current_Quantity", "Current_Quantity", "")),
          reorder_Quantity: toStr(getItemAttr(itemData, "reorder_Quantity", "Reorder_Quantity", "")),
          reorder_Level: toStr(getItemAttr(itemData, "reorder_Level", "Reorder_Level", "")),
          availableQuantity: toStr(getItemAttr(itemData, "availableQuantity", "AvailableQuantity", "")),
          orderQuantityLimit: toStr(getItemAttr(itemData, "orderQuantityLimit", "OrderQuantityLimit", "")),
          menuItem_IsBuyingProduct: !!getItemAttr(itemData, "menuItem_IsBuyingProduct", "MenuItem_IsBuyingProduct", false),
          menuItem_IsStockingProduct: !!getItemAttr(itemData, "menuItem_IsStockingProduct", "MenuItem_IsStockingProduct", false),
          menuItem_SourceType: getItemAttr(itemData, "menuItem_SourceType", "MenuItem_SourceType", "Purchase") || "Purchase",
          menuItem_HasRecipe: !!getItemAttr(itemData, "menuItem_HasRecipe", "MenuItem_HasRecipe", false),
          menuItem_RecipeId: toStr(getItemAttr(itemData, "menuItem_RecipeId", "MenuItem_RecipeId", "")),
          menuItem_RecipeType: getItemAttr(itemData, "menuItem_RecipeType", "MenuItem_RecipeType", "Made to order") || "Made to order",
          menuItem_IsTrackBatch: !!getItemAttr(itemData, "menuItem_IsTrackBatch", "MenuItem_IsTrackBatch", false),
          menuItem_IsTrackStock: !!getItemAttr(itemData, "menuItem_IsTrackStock", "MenuItem_IsTrackStock", false),
          menuItem_SellingMethodBoth: !!getItemAttr(itemData, "menuItem_SellingMethodBoth", "MenuItem_SellingMethodBoth", false),
          menuItem_PurchaseContainerType: getItemAttr(itemData, "menuItem_PurchaseContainerType", "MenuItem_PurchaseContainerType", "Individual") || "Individual",
          menuItem_PurchaseContainerQty: parseInt(getItemAttr(itemData, "menuItem_PurchaseContainerQty", "MenuItem_PurchaseContainerQty", 1), 10) || 1,
          menuItem_MeasurementType: getItemAttr(itemData, "menuItem_MeasurementType", "MenuItem_MeasurementType", "") || "",
          menuItem_DefaultSize: toStr(getItemAttr(itemData, "menuItem_DefaultSize", "MenuItem_DefaultSize", "")),
          menuItem_DefaultSizeUnit: getItemAttr(itemData, "menuItem_DefaultSizeUnit", "MenuItem_DefaultSizeUnit", "") || "",
          menuitem_DefaultSizeInBaseUnit: toFloat(getItemAttr(itemData, "menuitem_DefaultSizeInBaseUnit", "Menuitem_DefaultSizeInBaseUnit", 0)),
        };
        setInventoryState(initialState);
      }
    } catch (error) {
      showErrorToast("Failed to load item details");
    } finally {
      setInitialLoading(false);
    }
  }, [itemId, CompID]);

  useEffect(() => {
    if (!hasFetchedDetailsRef.current) {
      hasFetchedDetailsRef.current = true;
      fetchItemDetails();
    }
  }, [fetchItemDetails]);

  useEffect(() => {
    if (CompID) fetchCompanyProfile();
  }, [CompID, fetchCompanyProfile]);

  // Check if company has branches (only show branch UI if branches exist)
  useEffect(() => {
    const checkHasBranches = async () => {
      if (!CompID || !isParentCompany) {
        setHasBranches(false);
        return;
      }
      try {
        const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, {
          params: { CompID },
        });
        const branches = response.data?.data || [];
        // Normalize types/casing because APIs may return string IDs or camelCase fields.
        const toInt = (v) => {
          if (v === null || v === undefined) return null;
          const n = typeof v === "number" ? v : parseInt(v, 10);
          return Number.isFinite(n) ? n : null;
        };
        const compIdInt = toInt(CompID);
        // Check if there are actual child branches
        const hasChildBranches = Array.isArray(branches) && branches.some((b) => {
          const parentComId = toInt(b?.ParentComID ?? b?.parentComID);
          const branchCompId = toInt(b?.CompID ?? b?.compID);
          return parentComId !== null
            && compIdInt !== null
            && parentComId === compIdInt
            && branchCompId !== compIdInt;
        });
        setHasBranches(hasChildBranches);
      } catch (err) {
        setHasBranches(false);
      }
    };
    checkHasBranches();
  }, [CompID, isParentCompany]);

  // Fetch stock entries
  const fetchStockEntries = useCallback(async () => {
    if (!itemId || !CompID) return;

    try {
      setLoadingStock(true);
      const response = await axiosInstance.get(APIs.GET_INVENTORY_STOCK_BY_MENU_ITEM, {
        params: { MenuItem_ID: itemId, CompID: CompID },
      });

      const stockData = response.data.data || response.data || [];
      setStockEntries(Array.isArray(stockData) ? stockData : []);
    } catch (error) {
      console.error("Failed to load stock entries:", error);
      setStockEntries([]);
    } finally {
      setLoadingStock(false);
    }
  }, [itemId, CompID]);

  useEffect(() => {
    if (itemId && CompID && inventoryState?.menuItem_IsTrackStock) {
      fetchStockEntries();
    }
  }, [itemId, CompID, inventoryState?.menuItem_IsTrackStock, fetchStockEntries]);

  // Initialize variants list when item details load (normalize types so unsaved check is stable)
  useEffect(() => {
    if (!itemData) {
      setVariantItems([]);
      return;
    }
    const list = Array.isArray(itemData?.products) ? itemData.products : [];
    const cleaned = list
      .map((v) => {
        const base = { ...v };
        const qty = base?.menuItem_PurchaseContainerQty ?? base?.MenuItem_PurchaseContainerQty ?? 1;
        base.menuItem_PurchaseContainerQty = parseInt(qty, 10) || 1;
        return base;
      })
      .filter((v) => toInt(v?.menuItem_ID ?? v?.MenuItem_ID) > 0);
    setVariantItems(cleaned);
  }, [itemData]);

  // Capture baseline AFTER load + post-load hydration effects settle.
  useEffect(() => {
    if (initialLoading) {
      baselineReadyRef.current = false;
      setBaselineReady(false);
      setHasUnsavedChanges(false);
      return;
    }

    if (baselineReadyRef.current) return;

    // Wait until item data has been applied (variants derived from itemData)
    if (!itemData) return;

    const timer = setTimeout(() => {
      if (baselineReadyRef.current) return;
      captureBaselineSnapshot();
    }, 350);

    return () => clearTimeout(timer);
  }, [initialLoading, itemData, inventoryState, variantItems, captureBaselineSnapshot]);

  // Check for unsaved changes only after baseline snapshot is ready
  useEffect(() => {
    if (!baselineReady || !initialInventoryState) {
      setHasUnsavedChanges(false);
      return;
    }

    const inventoryChanged = stableStringify(inventoryState) !== stableStringify(initialInventoryState);
    const variantsChanged = showVariantOverview &&
      stableStringify(variantItems) !== stableStringify(initialVariantItems);

    setHasUnsavedChanges(inventoryChanged || variantsChanged);
  }, [baselineReady, inventoryState, variantItems, initialInventoryState, initialVariantItems, showVariantOverview]);

  const fetchAssignedBranches = useCallback(async () => {
    if (!CompID || !itemId) return [];
    try {
      const response = await axiosInstance.get(`${APIs.GET_ASSIGNED_BRANCHES_FOR_PRODUCT}/${itemId}/${CompID}`);
      const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
      const assigned = rows
        .filter((a) => a?.isAssigned)
        .map((a) => ({
          branchCompId: toInt(a?.branchCompID ?? a?.BranchCompID),
          branchName:
            a?.branchName ??
            a?.BranchName ??
            a?.compName ??
            a?.CompName ??
            a?.branchCompany?.compName ??
            a?.branchCompany?.CompName ??
            "Branch",
        }))
        .filter((b) => b.branchCompId > 0);
      return assigned;
    } catch (err) {
      return [];
    }
  }, [CompID, itemId]);

  useEffect(() => {
    // Parent company needs branch list for:
    // - variant overview (when item has variants)
    // - non-variant branch stock overview
    if (!isParentCompany) {
      setAssignedBranches([]);
      return;
    }
    if (hasVariants && variantItems.length === 0) {
      setAssignedBranches([]);
      return;
    }
    fetchAssignedBranches().then(setAssignedBranches);
  }, [isParentCompany, hasVariants, variantItems.length, fetchAssignedBranches]);

  const ensureVariantInMap = useCallback(
    (variant) => {
      const variantId = toInt(variant?.menuItem_ID ?? variant?.MenuItem_ID);
      if (!variantId) return;

      setVariantStockMap((prev) => {
        const existing = prev[variantId];
        const existingBranches = existing?.branches || {};

        const mergedBranches = { ...existingBranches };
        (assignedBranches || []).forEach((b) => {
          if (!mergedBranches[b.branchCompId]) {
            mergedBranches[b.branchCompId] = {
              branchCompId: b.branchCompId,
              branchName: b.branchName,
              totalQty: null,
              loading: false,
              expanded: false,
              entries: null,
            };
          }
        });

        const trackStock = !!(variant?.menuItem_IsTrackStock ?? variant?.MenuItem_IsTrackStock);

        return {
          ...prev,
          [variantId]: {
            variantId,
            trackStock,
            parentTotalQty: existing?.parentTotalQty ?? null,
            parentLoading: existing?.parentLoading ?? false,
            parentExpanded: existing?.parentExpanded ?? false,
            parentEntries: existing?.parentEntries ?? null,
            branchesSectionExpanded: existing?.branchesSectionExpanded ?? false,
            branches: mergedBranches,
          },
        };
      });
    },
    [assignedBranches]
  );

  // Seed state for all variants
  useEffect(() => {
    if (!isParentCompany || !hasVariants || variantItems.length === 0) return;
    variantItems.forEach((v) => ensureVariantInMap(v));
  }, [isParentCompany, hasVariants, variantItems, ensureVariantInMap]);

  const fetchParentTotalForVariant = useCallback(
    async (variantId) => {
      const id = toInt(variantId);
      if (!id || !CompID) return;
      const found = (Array.isArray(variantItems) ? variantItems : []).find(
        (v) => toInt(v?.menuItem_ID ?? v?.MenuItem_ID) === id
      );
      const trackStock = !!(found?.menuItem_IsTrackStock ?? found?.MenuItem_IsTrackStock);
      if (!trackStock) {
        setVariantStockMap((prev) => ({
          ...prev,
          [id]: { ...(prev[id] || {}), parentTotalQty: 0 },
        }));
        return;
      }

      setVariantStockMap((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), parentLoading: true },
      }));
      try {
        const res = await axiosInstance.get(APIs.GET_INVENTORY_STOCK_BY_MENU_ITEM, {
          params: { MenuItem_ID: id, CompID: CompID },
        });
        const entries = getEntriesFromResponse(res);
        const total = sumStockQty(entries);
        setVariantStockMap((prev) => ({
          ...prev,
          [id]: { ...(prev[id] || {}), parentTotalQty: total, parentLoading: false },
        }));
      } catch (e) {
        setVariantStockMap((prev) => ({
          ...prev,
          [id]: { ...(prev[id] || {}), parentTotalQty: 0, parentLoading: false },
        }));
      }
    },
    [CompID, variantItems]
  );

  const fetchBranchTotalForVariant = useCallback(
    async (variantId, branchCompId) => {
      const vId = toInt(variantId);
      const bId = toInt(branchCompId);
      if (!vId || !bId || !CompID) return;

      const found = (Array.isArray(variantItems) ? variantItems : []).find(
        (v) => toInt(v?.menuItem_ID ?? v?.MenuItem_ID) === vId
      );
      const trackStock = !!(found?.menuItem_IsTrackStock ?? found?.MenuItem_IsTrackStock);
      if (!trackStock) {
        setVariantStockMap((prev) => ({
          ...prev,
          [vId]: {
            ...(prev[vId] || {}),
            branches: {
              ...(prev[vId]?.branches || {}),
              [bId]: { ...(prev[vId]?.branches?.[bId] || {}), totalQty: 0 },
            },
          },
        }));
        return;
      }

      setVariantStockMap((prev) => ({
        ...prev,
        [vId]: {
          ...(prev[vId] || {}),
          branches: {
            ...(prev[vId]?.branches || {}),
            [bId]: { ...(prev[vId]?.branches?.[bId] || {}), loading: true },
          },
        },
      }));
      try {
        const res = await axiosInstance.get(APIs.GET_INVENTORY_STOCK_FOR_BRANCH_VARIANT, {
          params: { parentCompId: CompID, branchCompId: bId, menuItemId: vId, includeInactive: false },
        });
        const entries = getEntriesFromResponse(res);
        const total = sumStockQty(entries);
        setVariantStockMap((prev) => ({
          ...prev,
          [vId]: {
            ...(prev[vId] || {}),
            branches: {
              ...(prev[vId]?.branches || {}),
              [bId]: { ...(prev[vId]?.branches?.[bId] || {}), totalQty: total, loading: false },
            },
          },
        }));
      } catch (e) {
        setVariantStockMap((prev) => ({
          ...prev,
          [vId]: {
            ...(prev[vId] || {}),
            branches: {
              ...(prev[vId]?.branches || {}),
              [bId]: { ...(prev[vId]?.branches?.[bId] || {}), totalQty: 0, loading: false },
            },
          },
        }));
      }
    },
    [CompID, variantItems]
  );

  const loadVariantOverviewTotals = useCallback(async () => {
    if (!isParentCompany || !hasVariants || variantItems.length === 0) return;
    if (!Array.isArray(assignedBranches) || assignedBranches.length === 0) return;

    try {
      setLoadingVariantOverview(true);

      // Parent totals
      await Promise.allSettled(variantItems.map((v) => fetchParentTotalForVariant(toInt(v?.menuItem_ID ?? v?.MenuItem_ID))));

      // Branch totals
      const branchIds = assignedBranches.map((b) => b.branchCompId);
      for (const v of variantItems) {
        const vId = toInt(v?.menuItem_ID ?? v?.MenuItem_ID);
        if (!vId) continue;
        await Promise.allSettled(branchIds.map((bId) => fetchBranchTotalForVariant(vId, bId)));
      }
    } finally {
      setLoadingVariantOverview(false);
    }
  }, [isParentCompany, hasVariants, variantItems, assignedBranches, fetchParentTotalForVariant, fetchBranchTotalForVariant]);

  // Only load variant totals on initial load, not on every variantItems change
  useEffect(() => {
    if (!isParentCompany || !hasVariants) {
      hasLoadedVariantTotalsRef.current = false;
      return;
    }
    if (variantItems.length === 0 || assignedBranches.length === 0) {
      hasLoadedVariantTotalsRef.current = false;
      return;
    }
    
    // Only load once automatically, after that user must click "Refresh Stock"
    if (!hasLoadedVariantTotalsRef.current) {
      hasLoadedVariantTotalsRef.current = true;
      loadVariantOverviewTotals();
    }
  }, [isParentCompany, hasVariants, variantItems.length, assignedBranches.length, loadVariantOverviewTotals]);

  useEffect(() => {
    if (!showVariantOverview || !focusVariantId) return;
    const el = document.getElementById(`variant-inventory-${focusVariantId}`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [showVariantOverview, focusVariantId, variantItems.length]);

  // Auto-update IsBuyingProduct based on Source Type (only update if different to prevent unnecessary re-renders)
  useEffect(() => {
    const isPurchase = inventoryState.menuItem_SourceType === "Purchase";
    const currentIsBuying = inventoryState.menuItem_IsBuyingProduct || false;
    
    if (isPurchase !== currentIsBuying) {
      setInventoryState((prev) => ({
        ...prev,
        menuItem_IsBuyingProduct: isPurchase,
      }));
    }
  }, [inventoryState.menuItem_SourceType, inventoryState.menuItem_IsBuyingProduct]);

  // Auto-reset quantity to 1 when Individual is selected (only update if different to prevent unnecessary re-renders)
  useEffect(() => {
    if (inventoryState.menuItem_PurchaseContainerType === "Individual") {
      const currentQty = parseInt(inventoryState.menuItem_PurchaseContainerQty) || 1;
      if (currentQty !== 1) {
        setInventoryState((prev) => ({
          ...prev,
          menuItem_PurchaseContainerQty: 1,
        }));
      }
    }
  }, [inventoryState.menuItem_PurchaseContainerType, inventoryState.menuItem_PurchaseContainerQty]);

  const handleInputChange = (e, fieldName) => {
    let value = e.target.value;

    // Convert display units back to base units for quantity fields
    if ((fieldName === "current_Quantity" || fieldName === "reorder_Level") &&
      inventoryState.menuItem_MeasurementType &&
      inventoryState.menuItem_MeasurementType !== "Unit" &&
      inventoryState.menuItem_DefaultSize) {

      const displayValue = parseFloat(value) || 0;
      const defaultSize = parseFloat(inventoryState.menuItem_DefaultSize) || 1;

      // Convert from display units to base units
      const baseValue = displayValue / defaultSize;
      value = baseValue.toString();
    }

    // Update state with related fields in a single update to prevent multiple re-renders
    setInventoryState((prev) => {
      const updates = { [fieldName]: value };
      
      // If SourceType changes, also update IsBuyingProduct in the same update
      if (fieldName === "menuItem_SourceType") {
        updates.menuItem_IsBuyingProduct = value === "Purchase";
      }
      
      // If PurchaseContainerType changes to Individual, reset quantity in the same update
      if (fieldName === "menuItem_PurchaseContainerType" && value === "Individual") {
        updates.menuItem_PurchaseContainerQty = 1;
      }
      
      return { ...prev, ...updates };
    });
  };

  const handleToggle = (fieldName) => {
    setInventoryState((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  // Helper function to display value or empty string if "0" or "0.00"
  const getDisplayValue = (value) => {
    if (!value || value === "0" || value === "0.00" || parseFloat(value) === 0) {
      return "";
    }
    return value;
  };

  // Helper function to convert quantity to display units and get unit label
  const getDisplayQuantity = (quantity, fieldName = "Current Quantity") => {
    if (!quantity || parseFloat(quantity) === 0) {
      const unitLabel = getUnitLabel();
      return { value: "", label: `${fieldName}${unitLabel ? ` (${unitLabel})` : ""}` };
    }

    // For Unit type, no conversion needed
    if (inventoryState.menuItem_MeasurementType === "Unit" || !inventoryState.menuItem_MeasurementType) {
      const unitLabel = getUnitLabel();
      return { value: quantity, label: `${fieldName}${unitLabel ? ` (${unitLabel})` : ""}` };
    }

    // For Weight/Volume, convert from base units to display units
    const baseQuantity = parseFloat(quantity);
    const defaultSize = parseFloat(inventoryState.menuItem_DefaultSize) || 1;

    if (defaultSize > 0) {
      const displayQuantity = baseQuantity * defaultSize;
      const unitLabel = getUnitLabel();
      return {
        value: displayQuantity.toFixed(2),
        label: `${fieldName}${unitLabel ? ` (${unitLabel})` : ""}`
      };
    }

    const unitLabel = getUnitLabel();
    return { value: quantity, label: `${fieldName}${unitLabel ? ` (${unitLabel})` : ""}` };
  };

  // Helper function to get the unit label for display
  const getUnitLabel = () => {
    if (inventoryState.menuItem_MeasurementType === "Unit" || !inventoryState.menuItem_MeasurementType) {
      return "";
    }

    if (inventoryState.menuItem_DefaultSizeUnit) {
      return inventoryState.menuItem_DefaultSizeUnit;
    }

    // Fallback based on measurement type
    switch (inventoryState.menuItem_MeasurementType) {
      case "Weight":
        return "kg";
      case "Volume":
        return "L";
      default:
        return "";
    }
  };

  // Helper function to get the base unit name for the measurement type
  const getBaseUnitLabel = () => {
    if (!inventoryState.menuItem_MeasurementType) {
      return "";
    }

    switch (inventoryState.menuItem_MeasurementType) {
      case "Weight":
        return "kg";
      case "Volume":
        return "L";
      case "Unit":
        return ""; // Unit type doesn't have a base unit label
      default:
        return "";
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!itemId || !CompID) {
      showErrorToast("Missing required parameters");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        menuItem_ID: parseInt(itemId),
        localizeID: "en",
        compID: CompID,
        current_Quantity: toFloat(getItemAttr(itemData, "current_Quantity", "Current_Quantity", 0)),
        reorder_Quantity: parseFloat(inventoryState?.reorder_Quantity) || 0,
        reorder_Level: parseFloat(inventoryState?.reorder_Level) || 0,
        availableQuantity: parseFloat(inventoryState?.availableQuantity) || null,
        orderQuantityLimit: parseInt(inventoryState?.orderQuantityLimit) || 0,
        MenuItem_IsBuyingProduct: inventoryState?.menuItem_IsBuyingProduct ?? false,
        MenuItem_IsStockingProduct: inventoryState?.menuItem_IsStockingProduct ?? false,
        MenuItem_SourceType: inventoryState?.menuItem_SourceType || "Purchase",
        MenuItem_HasRecipe: !!getItemAttr(itemData, "menuItem_HasRecipe", "MenuItem_HasRecipe", false),
        MenuItem_RecipeId: parseInt(getItemAttr(itemData, "menuItem_RecipeId", "MenuItem_RecipeId", 0), 10) || 0,
        MenuItem_RecipeType: getItemAttr(itemData, "menuItem_RecipeType", "MenuItem_RecipeType", "Made to order") || "Made to order",
        MenuItem_IsTrackBatch: inventoryState?.menuItem_IsTrackBatch ?? false,
        MenuItem_IsTrackStock: inventoryState?.menuItem_IsTrackStock ?? false,
        MenuItem_SellingMethodBoth: !!getItemAttr(itemData, "menuItem_SellingMethodBoth", "MenuItem_SellingMethodBoth", false),
        MenuItem_IsSellingProduct: !!getItemAttr(itemData, "menuItem_IsSellingProduct", "MenuItem_IsSellingProduct", false),
        MenuItem_CostPrice: getItemAttr(itemData, "menuItem_CostPrice", "MenuItem_CostPrice", null) ?? null,
        MenuItem_PurchaseContainerType: inventoryState?.menuItem_PurchaseContainerType || "Individual",
        MenuItem_PurchaseContainerQty: parseInt(inventoryState?.menuItem_PurchaseContainerQty, 10) || 1,
      };

      const response = await axiosInstance.put(APIs.PUT_UPDATE_ITEM_INVENTORY, payload);

      if (response.data.status) {
        showSuccessToast("Inventory updated successfully");
        navigate("/items/manageItems");
      } else {
        showErrorToast(response.data.message || "Failed to update inventory");
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to update inventory");
    } finally {
      setLoading(false);
    }
  };

  const updateVariantTrackStock = (variantId, checked) => {
    const vId = toInt(variantId);
    if (!vId) return;

    // Only update local state - no API call
    // Use callback form to ensure we're working with latest state
    setVariantItems((prev) => {
      const updated = (Array.isArray(prev) ? prev : []).map((v) => {
        const id = toInt(v?.menuItem_ID ?? v?.MenuItem_ID);
        if (id !== vId) return v;
        return { ...v, menuItem_IsTrackStock: !!checked };
      });
      return updated;
    });
    
    setVariantStockMap((prev) => ({
      ...prev,
      [vId]: { ...(prev[vId] || {}), trackStock: !!checked },
    }));
  };

  const updateVariantLocal = useCallback((variantId, patch) => {
    const vId = toInt(variantId);
    if (!vId) return;
    
    // Use callback form to ensure we're working with latest state
    setVariantItems((prev) => {
      const updated = (Array.isArray(prev) ? prev : []).map((v) => {
        const id = toInt(v?.menuItem_ID ?? v?.MenuItem_ID);
        if (id !== vId) return v;
        return { ...v, ...patch };
      });
      return updated;
    });
  }, []);

  const saveAllInventorySettings = async (shouldExit = false) => {
    if (!CompID) {
      showErrorToast("Missing company ID");
      return false;
    }

    try {
      setLoading(true);

      if (showVariantOverview) {
        // Save all variants
        const variantPromises = variantItems.map(async (v) => {
          const vId = toInt(v?.menuItem_ID ?? v?.MenuItem_ID);
          if (!vId) return null;

          const sourceType = (v?.menuItem_SourceType ?? v?.MenuItem_SourceType ?? "Purchase").toString();
          const purchaseContainerType = (v?.menuItem_PurchaseContainerType ?? v?.MenuItem_PurchaseContainerType ?? "Individual").toString();
          const purchaseContainerQtyRaw = v?.menuItem_PurchaseContainerQty ?? v?.MenuItem_PurchaseContainerQty ?? 1;
          const purchaseContainerQty =
            purchaseContainerType === "Individual" ? 1 : (parseInt(purchaseContainerQtyRaw, 10) || 1);

          const isTrackStock = !!(v?.menuItem_IsTrackStock ?? v?.MenuItem_IsTrackStock);
          const isTrackBatch = !!(v?.menuItem_IsTrackBatch ?? v?.MenuItem_IsTrackBatch);
          const isStockingProduct = !!(v?.menuItem_IsStockingProduct ?? v?.MenuItem_IsStockingProduct);
          const isBuyingProduct = sourceType === "Purchase";

          const itemRes = await axiosInstance.get(APIs.GET_ITEM_BY_ID, {
            params: { CompID, MenuItem_ID: vId },
          });
          const currentItemData = itemRes?.data?.data || itemRes?.data?.Data || itemRes?.data || {};

          const payload = {
            menuItem_ID: vId,
            localizeID: "en",
            compID: CompID,
            current_Quantity: toFloat(getItemAttr(currentItemData, "current_Quantity", "Current_Quantity", 0)),
            reorder_Quantity: toFloat(getItemAttr(currentItemData, "reorder_Quantity", "Reorder_Quantity", 0)),
            reorder_Level: toFloat(getItemAttr(currentItemData, "reorder_Level", "Reorder_Level", 0)),
            availableQuantity: getItemAttr(currentItemData, "availableQuantity", "AvailableQuantity", null) ?? null,
            orderQuantityLimit: parseInt(getItemAttr(currentItemData, "orderQuantityLimit", "OrderQuantityLimit", 0), 10) || 0,
            MenuItem_IsBuyingProduct: isBuyingProduct,
            MenuItem_IsStockingProduct: isStockingProduct,
            MenuItem_SourceType: sourceType,
            MenuItem_HasRecipe: !!getItemAttr(currentItemData, "menuItem_HasRecipe", "MenuItem_HasRecipe", false),
            MenuItem_RecipeId: parseInt(getItemAttr(currentItemData, "menuItem_RecipeId", "MenuItem_RecipeId", 0), 10) || 0,
            MenuItem_RecipeType: getItemAttr(currentItemData, "menuItem_RecipeType", "MenuItem_RecipeType", "Made to order") || "Made to order",
            MenuItem_IsTrackBatch: isTrackBatch,
            MenuItem_IsTrackStock: isTrackStock,
            MenuItem_SellingMethodBoth: !!getItemAttr(currentItemData, "menuItem_SellingMethodBoth", "MenuItem_SellingMethodBoth", false),
            MenuItem_IsSellingProduct: !!getItemAttr(currentItemData, "menuItem_IsSellingProduct", "MenuItem_IsSellingProduct", false),
            MenuItem_CostPrice: getItemAttr(currentItemData, "menuItem_CostPrice", "MenuItem_CostPrice", null) ?? null,
            MenuItem_PurchaseContainerType: purchaseContainerType,
            MenuItem_PurchaseContainerQty: purchaseContainerQty,
          };

          return axiosInstance.put(APIs.PUT_UPDATE_ITEM_INVENTORY, payload);
        });

        const results = await Promise.allSettled(variantPromises);
        const failed = results.filter((r) => r.status === "rejected" || (r.status === "fulfilled" && !r.value?.data?.status));
        
        if (failed.length > 0) {
          showErrorToast(`Failed to save ${failed.length} variant(s).`);
          return false;
        } else {
          showSuccessToast("All variant inventory settings saved successfully.");
          captureBaselineSnapshot();
          // Refresh all variant totals
          await Promise.allSettled(
            variantItems.map((v) => {
              const vId = toInt(v?.menuItem_ID ?? v?.MenuItem_ID);
              if (!vId) return Promise.resolve();
              return Promise.allSettled([
                fetchParentTotalForVariant(vId),
                ...(assignedBranches || []).map((b) => fetchBranchTotalForVariant(vId, b.branchCompId)),
              ]);
            })
          );
          if (shouldExit) {
            navigate("/items/manageItems");
          }
          return true;
        }
      } else {
        // Save non-variant item
        if (!itemId) {
          showErrorToast("Missing item ID");
          return;
        }

        const payload = {
          menuItem_ID: parseInt(itemId),
          localizeID: "en",
          compID: CompID,
          current_Quantity: toFloat(getItemAttr(itemData, "current_Quantity", "Current_Quantity", 0)),
          reorder_Quantity: parseFloat(inventoryState?.reorder_Quantity) || 0,
          reorder_Level: parseFloat(inventoryState?.reorder_Level) || 0,
          availableQuantity: parseFloat(inventoryState?.availableQuantity) || null,
          orderQuantityLimit: parseInt(inventoryState?.orderQuantityLimit) || 0,
          MenuItem_IsBuyingProduct: inventoryState?.menuItem_IsBuyingProduct ?? false,
          MenuItem_IsStockingProduct: inventoryState?.menuItem_IsStockingProduct ?? false,
          MenuItem_SourceType: inventoryState?.menuItem_SourceType || "Purchase",
          MenuItem_HasRecipe: !!getItemAttr(itemData, "menuItem_HasRecipe", "MenuItem_HasRecipe", false),
          MenuItem_RecipeId: parseInt(getItemAttr(itemData, "menuItem_RecipeId", "MenuItem_RecipeId", 0), 10) || 0,
          MenuItem_RecipeType: getItemAttr(itemData, "menuItem_RecipeType", "MenuItem_RecipeType", "Made to order") || "Made to order",
          MenuItem_IsTrackBatch: inventoryState?.menuItem_IsTrackBatch ?? false,
          MenuItem_IsTrackStock: inventoryState?.menuItem_IsTrackStock ?? false,
          MenuItem_SellingMethodBoth: !!getItemAttr(itemData, "menuItem_SellingMethodBoth", "MenuItem_SellingMethodBoth", false),
          MenuItem_IsSellingProduct: !!getItemAttr(itemData, "menuItem_IsSellingProduct", "MenuItem_IsSellingProduct", false),
          MenuItem_CostPrice: getItemAttr(itemData, "menuItem_CostPrice", "MenuItem_CostPrice", null) ?? null,
          MenuItem_PurchaseContainerType: inventoryState?.menuItem_PurchaseContainerType || "Individual",
          MenuItem_PurchaseContainerQty: parseInt(inventoryState?.menuItem_PurchaseContainerQty, 10) || 1,
        };

        const response = await axiosInstance.put(APIs.PUT_UPDATE_ITEM_INVENTORY, payload);

        if (response.data.status) {
          showSuccessToast("Inventory updated successfully");
          captureBaselineSnapshot();
          // Refresh stock entries
          if (inventoryState?.menuItem_IsTrackStock) {
            await fetchStockEntries();
          }
          if (shouldExit) {
            navigate("/items/manageItems");
          }
          return true;
        } else {
          showErrorToast(response.data.message || "Failed to update inventory");
          return false;
        }
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to save inventory settings");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change from ItemTabBar
  const handleTabChange = async (action, tab) => {
    if (action === "save") {
      const success = await saveAllInventorySettings(false);
      return success;
    } else if (action === "discard") {
      // Reset to initial state
      if (initialInventoryState) {
        setInventoryState(JSON.parse(JSON.stringify(initialInventoryState)));
      }
      if (initialVariantItems.length > 0) {
        setVariantItems(JSON.parse(JSON.stringify(initialVariantItems)));
      }
      setHasUnsavedChanges(false);
      return true;
    }
    return true;
  };

  const toggleParentLots = async (variantId) => {
    const vId = toInt(variantId);
    if (!vId || !CompID) return;

    const isExpanded = !!variantStockMap?.[vId]?.parentExpanded;
    const nextExpanded = !isExpanded;

    setVariantStockMap((prev) => ({
      ...prev,
      [vId]: { ...(prev[vId] || {}), parentExpanded: nextExpanded },
    }));

    if (!nextExpanded) return;
    if (!variantStockMap?.[vId]?.trackStock) return;
    if (variantStockMap?.[vId]?.parentEntries) return;

    setVariantStockMap((prev) => ({
      ...prev,
      [vId]: { ...(prev[vId] || {}), parentLoading: true },
    }));

    try {
      const res = await axiosInstance.get(APIs.GET_INVENTORY_STOCK_BY_MENU_ITEM, {
        params: { MenuItem_ID: vId, CompID: CompID },
      });
      const entries = getEntriesFromResponse(res);
      setVariantStockMap((prev) => ({
        ...prev,
        [vId]: { ...(prev[vId] || {}), parentEntries: entries, parentLoading: false },
      }));
    } catch {
      setVariantStockMap((prev) => ({
        ...prev,
        [vId]: { ...(prev[vId] || {}), parentLoading: false },
      }));
    }
  };

  const toggleBranchesSection = (variantId) => {
    const vId = toInt(variantId);
    if (!vId) return;
    setVariantStockMap((prev) => ({
      ...prev,
      [vId]: {
        ...(prev[vId] || {}),
        branchesSectionExpanded: !!(prev[vId] && !prev[vId].branchesSectionExpanded),
      },
    }));
  };

  const toggleBranchLots = async (variantId, branchCompId) => {
    const vId = toInt(variantId);
    const bId = toInt(branchCompId);
    if (!vId || !bId || !CompID) return;

    const branchState = variantStockMap?.[vId]?.branches?.[bId] || {};
    const nextExpanded = !branchState.expanded;

    setVariantStockMap((prev) => ({
      ...prev,
      [vId]: {
        ...(prev[vId] || {}),
        branches: {
          ...(prev[vId]?.branches || {}),
          [bId]: { ...branchState, expanded: nextExpanded },
        },
      },
    }));

    if (!nextExpanded) return;
    if (!variantStockMap?.[vId]?.trackStock) return;
    if (branchState.entries) return;

    setVariantStockMap((prev) => ({
      ...prev,
      [vId]: {
        ...(prev[vId] || {}),
        branches: {
          ...(prev[vId]?.branches || {}),
          [bId]: { ...branchState, loading: true },
        },
      },
    }));

    try {
      const res = await axiosInstance.get(APIs.GET_INVENTORY_STOCK_FOR_BRANCH_VARIANT, {
        params: { parentCompId: CompID, branchCompId: bId, menuItemId: vId, includeInactive: false },
      });
      const entries = getEntriesFromResponse(res);
      setVariantStockMap((prev) => ({
        ...prev,
        [vId]: {
          ...(prev[vId] || {}),
          branches: {
            ...(prev[vId]?.branches || {}),
            [bId]: { ...branchState, entries, loading: false, expanded: true },
          },
        },
      }));
    } catch (e) {
      setVariantStockMap((prev) => ({
        ...prev,
        [vId]: {
          ...(prev[vId] || {}),
          branches: {
            ...(prev[vId]?.branches || {}),
            [bId]: { ...branchState, entries: [], loading: false, expanded: true },
          },
        },
      }));
    }
  };

  const loadNonVariantBranchTotals = useCallback(async () => {
    if (!isParentCompany || hasVariants) return;
    if (!itemId || !CompID) return;
    if (!inventoryState?.menuItem_IsTrackStock) return;
    if (!Array.isArray(assignedBranches) || assignedBranches.length === 0) return;

    try {
      setLoadingItemBranchTotals(true);

      // seed map
      setItemBranchStockMap((prev) => {
        const next = { ...(prev || {}) };
        assignedBranches.forEach((b) => {
          if (!next[b.branchCompId]) {
            next[b.branchCompId] = {
              branchCompId: b.branchCompId,
              branchName: b.branchName,
              totalQty: null,
              loading: false,
              expanded: false,
              entries: null,
            };
          } else {
            next[b.branchCompId] = {
              ...next[b.branchCompId],
              branchName: b.branchName,
            };
          }
        });
        return next;
      });

      const masterId = toInt(itemId);
      await Promise.allSettled(
        assignedBranches.map(async (b) => {
          const bId = toInt(b.branchCompId);
          if (!bId) return;
          try {
            const res = await axiosInstance.get(APIs.GET_INVENTORY_STOCK_FOR_BRANCH_VARIANT, {
              params: { parentCompId: CompID, branchCompId: bId, menuItemId: masterId, includeInactive: false },
            });
            const entries = getEntriesFromResponse(res);
            const total = sumStockQty(entries);
            setItemBranchStockMap((prev) => ({
              ...(prev || {}),
              [bId]: { ...(prev?.[bId] || {}), totalQty: total, loading: false },
            }));
          } catch (e) {
            setItemBranchStockMap((prev) => ({
              ...(prev || {}),
              [bId]: { ...(prev?.[bId] || {}), totalQty: 0, loading: false },
            }));
          }
        })
      );
    } finally {
      setLoadingItemBranchTotals(false);
    }
  }, [isParentCompany, hasVariants, itemId, CompID, inventoryState?.menuItem_IsTrackStock, assignedBranches]);

  useEffect(() => {
    if (!isParentCompany || hasVariants) {
      setItemBranchStockMap({});
      return;
    }
    if (!inventoryState?.menuItem_IsTrackStock) {
      setItemBranchStockMap({});
      return;
    }
    if (!Array.isArray(assignedBranches) || assignedBranches.length === 0) {
      setItemBranchStockMap({});
      return;
    }
    loadNonVariantBranchTotals();
  }, [isParentCompany, hasVariants, inventoryState?.menuItem_IsTrackStock, assignedBranches.length, loadNonVariantBranchTotals]);

  const toggleItemBranchLots = async (branchCompId) => {
    const bId = toInt(branchCompId);
    if (!bId || !itemId || !CompID) return;
    if (!inventoryState?.menuItem_IsTrackStock) return;

    const current = itemBranchStockMap?.[bId] || {};
    const nextExpanded = !current.expanded;

    setItemBranchStockMap((prev) => ({
      ...(prev || {}),
      [bId]: { ...(prev?.[bId] || {}), expanded: nextExpanded },
    }));

    if (!nextExpanded) return;
    if (current.entries) return;

    setItemBranchStockMap((prev) => ({
      ...(prev || {}),
      [bId]: { ...(prev?.[bId] || {}), loading: true },
    }));

    try {
      const masterId = toInt(itemId);
      const res = await axiosInstance.get(APIs.GET_INVENTORY_STOCK_FOR_BRANCH_VARIANT, {
        params: { parentCompId: CompID, branchCompId: bId, menuItemId: masterId, includeInactive: false },
      });
      const entries = getEntriesFromResponse(res);
      setItemBranchStockMap((prev) => ({
        ...(prev || {}),
        [bId]: { ...(prev?.[bId] || {}), entries, loading: false, expanded: true },
      }));
    } catch (e) {
      setItemBranchStockMap((prev) => ({
        ...(prev || {}),
        [bId]: { ...(prev?.[bId] || {}), entries: [], loading: false, expanded: true },
      }));
    }
  };

  const itemName = itemData?.menuItem_Name ?? itemData?.MenuItem_Name ?? `Item #${itemId}`;

  const renderBreadcrumb = () => (
    <div className="d-flex align-items-center mb-3">
      <div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="#/">Home</a>
          </li>
          <li className="breadcrumb-item">
            <Link to="/items/manageItems">Manage Items</Link>
          </li>
          <li className="breadcrumb-item active">Update Inventory</li>
        </ol>
        <h1 className="page-header mb-0" style={{ fontSize: "1.35rem" }}>
          Update Item Inventory
        </h1>
        <p className="text-muted mb-0 mt-1" style={{ fontSize: "13px" }}>{itemName}</p>
      </div>
    </div>
  );

  if (initialLoading) {
    return (
      <div className="container-fluid p-3">
        <Preloader />
      </div>
    );
  }

  return (
    <div className="container-fluid p-3">
      {loading && <Preloader />}
      {renderBreadcrumb()}
      {itemId && (
        <>
          <ItemTabBar 
            itemId={itemId} 
            hasUnsavedChanges={hasUnsavedChanges}
            onTabChange={handleTabChange}
          />
          <div className="d-flex align-items-center justify-content-end gap-2 mb-2 flex-wrap">
            <button
              onClick={() => saveAllInventorySettings(false)}
              className="btn btn-rounded px-4 rounded-pill"
              disabled={loading}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: "none",
                color: "white",
                borderRadius: "8px",
                padding: "6px 14px",
                fontSize: "13px",
                fontWeight: "600",
                transition: "all 0.3s ease",
                boxShadow: `0 2px 8px ${themeColor}40`,
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                }
              }}
            >
              {loading ? "Saving..." : "Save"}
            </button>
            <button
              onClick={() => saveAllInventorySettings(true)}
              className="btn btn-rounded px-4 rounded-pill"
              disabled={loading}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: "none",
                color: "white",
                borderRadius: "8px",
                padding: "6px 14px",
                fontSize: "13px",
                fontWeight: "600",
                transition: "all 0.3s ease",
                boxShadow: `0 2px 8px ${themeColor}40`,
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                }
              }}
            >
              {loading ? "Saving..." : "Save and Exit"}
            </button>
          </div>
        </>
      )}

      {showVariantOverview ? (
        <Panel className="mb-3">
          <PanelHeader
            style={{
              backgroundColor: themeColor,
              color: "white",
              border: "none",
              borderRadius: "10px 10px 0 0",
              padding: "12px 20px",
              fontWeight: "600",
              fontSize: "15px",
            }}
          >
            <i className="fa fa-layer-group me-2"></i>
            Inventory by Variant 
          </PanelHeader>
          <PanelBody
            style={{
              padding: "16px 20px",
              backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
              borderRadius: "0 0 10px 10px",
              border: `1px solid ${themeColor}20`,
            }}
          >
            {loadingVariantOverview && (
              <div className="text-center py-3">
                <Preloader />
              </div>
            )}

            {variantItems.map((v) => {
              const vid = toInt(v?.menuItem_ID ?? v?.MenuItem_ID);
              const title = v?.menuItem_DefaultVariantTitle ?? v?.MenuItem_DefaultVariantTitle ?? "";
              const state = variantStockMap?.[vid] || {};
              const trackStock = !!state.trackStock;
              const parentTotal = state.parentTotalQty;
              const parentExpanded = !!state.parentExpanded;
              const branchesSectionExpanded = !!state.branchesSectionExpanded;
              // Hidden with Track Batch / Stocking switches
              // const trackBatch = !!(v?.menuItem_IsTrackBatch ?? v?.MenuItem_IsTrackBatch);
              const sourceType = (v?.menuItem_SourceType ?? v?.MenuItem_SourceType ?? "Purchase").toString();
              // const isStockingProduct = !!(v?.menuItem_IsStockingProduct ?? v?.MenuItem_IsStockingProduct);
              const purchaseContainerType = (v?.menuItem_PurchaseContainerType ?? v?.MenuItem_PurchaseContainerType ?? "Individual").toString();
              const purchaseContainerQty = v?.menuItem_PurchaseContainerQty ?? v?.MenuItem_PurchaseContainerQty ?? 1;

              return (
                <div
                  key={vid}
                  id={`variant-inventory-${vid}`}
                  className="mb-3"
                  style={{
                    padding: 12,
                    borderRadius: 12,
                    backgroundColor: isDarkMode ? "#1e2124" : "#f8f9fa",
                    border: `1px solid ${themeColor}20`,
                  }}
                >
                  <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
                    <div>
                      <div className="fw-bold" style={{ fontSize: 15 }}>
                        {title || "Variant"}
                      </div>

                    </div>

                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                      <div className="d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-bold" style={{ fontSize: 12 }}>
                            Track Stock
                          </span>
                          <ToggleSwitch
                            isChecked={trackStock}
                            onToggle={(checked) => updateVariantTrackStock(vid, checked)}
                            name={`variant_track_stock_${vid}`}
                            size="small"
                          />
                        </div>
                        {/* Hidden for now
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-bold" style={{ fontSize: 12 }}>
                            Track Batch
                          </span>
                          <ToggleSwitch
                            isChecked={trackBatch}
                            onToggle={(checked) => updateVariantLocal(vid, { menuItem_IsTrackBatch: !!checked })}
                            name={`variant_track_batch_${vid}`}
                            size="small"
                          />
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-bold" style={{ fontSize: 12 }}>
                            Is Stocking Product
                          </span>
                          <ToggleSwitch
                            isChecked={isStockingProduct}
                            onToggle={(checked) =>
                              updateVariantLocal(vid, { menuItem_IsStockingProduct: !!checked })
                            }
                            name={`variant_stocking_${vid}`}
                            size="small"
                          />
                        </div>
                        */}
                      </div>
                    </div>
                  </div>

                  <div
                    className="mt-2"
                    style={{
                      padding: 12,
                      borderRadius: 12,
                      backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                      border: `1px solid ${themeColor}20`,
                    }}
                  >
                    <div className="fw-bold mb-1" style={{ fontSize: 13 }}>
                      Product Type Settings
                    </div>

                    {/* Source Type and Purchase Container Settings */}

                    <div className="row g-2 mt-2">
                      <div className="col-md-4">
                        <label className="form-label fw-bold" style={{ fontSize: 12 }}>
                          Source Type
                        </label>
                        <select
                          className="form-select"
                          value={sourceType}
                          onChange={(e) =>
                            updateVariantLocal(vid, {
                              menuItem_SourceType: e.target.value,
                              menuItem_IsBuyingProduct: e.target.value === "Purchase",
                            })
                          }
                          style={{
                            borderRadius: 12,
                            backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
                            border: `1px solid ${themeColor}30`,
                            color: isDarkMode ? "#ffffff" : "#000000",
                          }}
                        >
                          <option value="Purchase">Purchase</option>
                          <option value="Made here">Made here</option>
                        </select>
                        {sourceType === "Made here" && (
                          <div className="mt-1">
                            <Link
                              to={`/recipes/createRecipe/${vid}`}
                              className="d-inline-flex align-items-center gap-1"
                              style={{
                                color: themeColor,
                                fontWeight: "600",
                                fontSize: "12px",
                                textDecoration: "none",
                              }}
                            >
                              <i className="fa fa-utensils"></i>
                              Manage Recipe
                            </Link>
                          </div>
                        )}
                      </div>
                      {sourceType === "Purchase" && (
                        <>
                          <div className="col-md-4">
                            <label className="form-label fw-bold" style={{ fontSize: 12 }}>
                              Purchase Container Type
                            </label>
                            <select
                              className="form-select"
                              value={purchaseContainerType}
                              onChange={(e) =>
                                updateVariantLocal(vid, {
                                  menuItem_PurchaseContainerType: e.target.value,
                                  menuItem_PurchaseContainerQty: e.target.value === "Individual" ? 1 : purchaseContainerQty,
                                })
                              }
                              style={{
                                borderRadius: 12,
                                backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
                                border: `1px solid ${themeColor}30`,
                                color: isDarkMode ? "#ffffff" : "#000000",
                              }}
                            >
                              <option value="Individual">Individual</option>
                              <option value="Boxes">Box</option>
                              <option value="Packages">Package</option>
                              <option value="Cartons">Carton</option>
                              <option value="Cases">Case</option>
                              <option value="Pallets">Pallet</option>
                              <option value="Bags">Bag</option>
                              <option value="Bottles">Bottle</option>
                              <option value="Cans">Can</option>
                            </select>
                          </div>

                          {purchaseContainerType !== "Individual" && (
                            <div className="col-md-4">
                              <label className="form-label fw-bold" style={{ fontSize: 12 }}>
                                Purchase Container Qty
                              </label>
                              <input
                                type="number"
                                min="1"
                                className="form-control"
                                value={purchaseContainerQty}
                                onChange={(e) =>
                                  updateVariantLocal(vid, {
                                    menuItem_PurchaseContainerQty: parseInt(e.target.value, 10) || 1,
                                  })
                                }
                                style={{
                                  borderRadius: 12,
                                  backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
                                  border: `1px solid ${themeColor}30`,
                                  color: isDarkMode ? "#ffffff" : "#000000",
                                }}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Stock & Branches card – Parent Qty visible by default, Branches expand on click */}
                    {trackStock && (
                      <div
                        className="mt-2 p-2 rounded"
                        style={{
                          border: `1px solid ${themeColor}20`,
                          backgroundColor: isDarkMode ? "#1e2124" : "#f8f9fa",
                        }}
                      >
                        {/* Parent Qty – always visible */}
                        <div
                          className="p-2 rounded d-flex align-items-center justify-content-between"
                          style={{
                            backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                            border: `1px solid ${themeColor}15`,
                            cursor: "pointer",
                          }}
                          onClick={() => toggleParentLots(vid)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === "Enter" && toggleParentLots(vid)}
                        >
                          <span className="fw-bold" style={{ fontSize: 12 }}>
                            Parent Qty:{" "}
                            {state.parentLoading
                              ? "Loading..."
                              : parentTotal === null || parentTotal === undefined
                                ? "-"
                                : toFloat(parentTotal).toFixed(2)}
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleParentLots(vid);
                            }}
                            style={{ borderRadius: 10 }}
                          >
                            {parentExpanded ? "Hide lots" : "View lots"}
                          </button>
                        </div>

                        {parentExpanded && (
                          <div className="mt-2">
                            {state.parentLoading ? (
                              <div className="text-center py-3">
                                <Preloader />
                              </div>
                            ) : (
                              renderStockEntriesTable(state.parentEntries || [], { compact: true })
                            )}
                          </div>
                        )}

                        {/* Branches – click to expand (hidden by default) */}
                        {hasBranches && (
                          <>
                            <div
                              className="mt-2 p-2 rounded d-flex align-items-center justify-content-between"
                              style={{
                                backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                                border: `1px solid ${themeColor}15`,
                                cursor: "pointer",
                              }}
                              onClick={() => toggleBranchesSection(vid)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => e.key === "Enter" && toggleBranchesSection(vid)}
                            >
                              <span className="fw-bold" style={{ fontSize: 12 }}>
                                Branches
                              </span>
                              <i
                                className={`fa fa-chevron-${branchesSectionExpanded ? "up" : "down"}`}
                                style={{ fontSize: 12, color: isDarkMode ? "#adb5bd" : "#6c757d" }}
                              />
                            </div>

                            {branchesSectionExpanded && (
                              <div className="mt-2">
                                {assignedBranches.length === 0 ? (
                                  <div className="text-muted py-2" style={{ fontSize: 12 }}>
                                    No branches assigned to this variant.
                                  </div>
                                ) : (
                                  <div className="table-responsive">
                                    <table className="table table-hover">
                                      <thead>
                                        <tr
                                          style={{
                                            backgroundColor: isDarkMode ? "#3c4043" : "#ffffff",
                                            borderBottom: `2px solid ${themeColor}`,
                                          }}
                                        >
                                          <th>Branch</th>
                                          <th style={{ width: 160 }}>Qty</th>
                                          <th style={{ width: 160 }}>Lots</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {assignedBranches.map((b) => {
                                          const bs = state?.branches?.[b.branchCompId] || {};
                                          const expanded = !!bs.expanded;
                                          const qty = bs.totalQty;
                                          return (
                                            <React.Fragment key={`${vid}_${b.branchCompId}`}>
                                              <tr>
                                                <td>{b.branchName}</td>
                                                <td>
                                                  {bs.loading
                                                    ? "Loading..."
                                                    : qty === null || qty === undefined
                                                      ? "-"
                                                      : toFloat(qty).toFixed(2)}
                                                </td>
                                                <td>
                                                  <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    disabled={!trackStock}
                                                    onClick={() => toggleBranchLots(vid, b.branchCompId)}
                                                    style={{ borderRadius: 10 }}
                                                  >
                                                    {expanded ? "Hide lots" : "View lots"}
                                                  </button>
                                                </td>
                                              </tr>
                                              {expanded && (
                                                <tr>
                                                  <td colSpan={3}>
                                                    {bs.loading ? (
                                                      <div className="text-center py-3">
                                                        <Preloader />
                                                      </div>
                                                    ) : (
                                                      renderStockEntriesTable(bs.entries || [], { compact: true })
                                                    )}
                                                  </td>
                                                </tr>
                                              )}
                                            </React.Fragment>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </PanelBody>
        </Panel>
      ) : (
        <>
          {/* Tracking Settings Panel */}
          <Panel className="mb-3">
            <PanelHeader
              style={{
                backgroundColor: themeColor,
                color: 'white',
                border: 'none',
                borderRadius: '10px 10px 0 0',
                padding: '12px 20px',
                fontWeight: '600',
                fontSize: '15px'
              }}
            >
              <i className="fa fa-clipboard-list me-2"></i>
              Tracking Settings
            </PanelHeader>
            <PanelBody
              style={{
                padding: '16px 20px',
                backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                borderRadius: '0 0 10px 10px',
                border: `1px solid ${themeColor}20`
              }}
            >
              {/* Tracking Toggles */}
              <div className="row g-2">
                <div className="col-md-6">
                  <div
                    className="d-flex align-items-center p-2 rounded"
                    style={{
                      backgroundColor: isDarkMode ? '#3c4043' : '#f8f9fa',
                      border: `1px solid ${themeColor}20`,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <ToggleSwitch
                      isChecked={inventoryState?.menuItem_IsTrackStock || false}
                      onToggle={() => handleToggle("menuItem_IsTrackStock")}
                      label="Track Stock"
                      name="menuItem_IsTrackStock"
                      themeColor={themeColor}
                    />
                  </div>
                </div>
                {/* Hidden for now
                <div className="col-md-6">
                  <div
                    className="d-flex align-items-center p-2 rounded"
                    style={{
                      backgroundColor: isDarkMode ? '#3c4043' : '#f8f9fa',
                      border: `1px solid ${themeColor}20`,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <ToggleSwitch
                      isChecked={inventoryState?.menuItem_IsTrackBatch || false}
                      onToggle={() => handleToggle("menuItem_IsTrackBatch")}
                      label="Track Batch"
                      name="menuItem_IsTrackBatch"
                      themeColor={themeColor}
                    />
                  </div>
                </div>
                */}
              </div>

              {/* Inventory Information - Only show when Track Stock is enabled */}
              {inventoryState?.menuItem_IsTrackStock && (
                <>
                  <div className="row g-2 mt-2">
                    <div className="col-12">
                      <h6 className="mb-1" style={{ color: isDarkMode ? '#ffffff' : '#000000', fontWeight: '600', fontSize: '14px' }}>
                        <i className="fa fa-warehouse me-2"></i>
                        Inventory Information
                      </h6>
                    </div>
                  </div>

                  <div className="row mb-2 g-2">
                    <div className="col-md-6">
                      <FormInput
                        label={`Current Quantity${getBaseUnitLabel() ? ` (${getBaseUnitLabel()})` : ""}`}
                        placeHolder="0"
                        type="number"
                        value={inventoryState?.current_Quantity || ""}
                        readOnly={true}
                      />
                    </div>
                    <div className="col-md-6">
                      <FormInput
                        label={getDisplayQuantity(inventoryState?.reorder_Level, "Safety Stock").label}
                        placeHolder="0"
                        type="number"
                        value={getDisplayQuantity(inventoryState?.reorder_Level, "Safety Stock").value}
                        onchange={(e) => handleInputChange(e, "reorder_Level")}
                      />
                    </div>
                  </div>
                </>
              )}


              {/* Product Type Settings - Moved to end */}
              <div className="row g-2 mt-2">
                <div className="col-md-12">
                  <h6 className="mb-1" style={{ color: isDarkMode ? '#ffffff' : '#000000', fontWeight: '600', fontSize: '14px' }}>
                    <i className="fa fa-tags me-2"></i>
                    Product Type Settings
                  </h6>
                </div>
                {/* Hidden for now
                <div className="col-md-12">
                  <div
                    className="d-flex align-items-center p-2 rounded"
                    style={{
                      backgroundColor: isDarkMode ? '#3c4043' : '#f8f9fa',
                      border: `1px solid ${themeColor}20`,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <ToggleSwitch
                      isChecked={inventoryState?.menuItem_IsStockingProduct || false}
                      onToggle={() => handleToggle("menuItem_IsStockingProduct")}
                      label="Is Stocking Product"
                      name="menuItem_IsStockingProduct"
                      themeColor={themeColor}
                    />
                  </div>
                </div>
                */}
              </div>

              <div className="row g-2 mt-2">
                <div className="col-md-6">
                  <label className="form-label fw-medium mb-1" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                    Source Type
                  </label>
                  <select
                    className="form-control"
                    value={inventoryState?.menuItem_SourceType || "Purchase"}
                    onChange={(e) => handleInputChange(e, "menuItem_SourceType")}
                    style={{
                      borderRadius: "8px",
                      border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                      background: isDarkMode ? "#2d3436" : "#F9F9F9",
                      color: isDarkMode ? "#ffffff" : "#000000",
                      padding: "8px 12px",
                    }}
                  >
                    <option value="Purchase">Purchase</option>
                    <option value="Made here">Made here</option>
                  </select>
                  {inventoryState?.menuItem_SourceType === "Made here" && itemId && (
                    <div className="mt-2">
                      <Link
                        to={`/recipes/createRecipe/${itemId}`}
                        className="d-inline-flex align-items-center gap-1"
                        style={{
                          color: themeColor,
                          fontWeight: "600",
                          fontSize: "13px",
                          textDecoration: "none",
                        }}
                      >
                        <i className="fa fa-utensils"></i>
                        Manage Recipe
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Purchase Container Settings - Only show when Source Type is "Purchase" */}
              {inventoryState?.menuItem_SourceType === "Purchase" && (
                <div className="row g-2 mt-2">
                  <div className="col-md-12">
                    <label className="form-label fw-medium mb-2" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                      <i className="fa fa-box me-2"></i>
                      Purchase Container Settings
                    </label>
                  </div>
                  <div className="col-md-12">
                    <div
                      className="p-2 rounded"
                      style={{
                        backgroundColor: isDarkMode ? '#3c4043' : '#f8f9fa',
                        border: `1px solid ${themeColor}20`,
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <div className="d-flex align-items-center flex-wrap gap-3">
                        <span style={{ color: isDarkMode ? '#ffffff' : '#000000', fontWeight: '500' }}>
                          This product is purchased
                        </span>
                        <select
                          className="form-control"
                          value={inventoryState?.menuItem_PurchaseContainerType || "Individual"}
                          onChange={(e) => handleInputChange(e, "menuItem_PurchaseContainerType")}
                          style={{
                            width: 'auto',
                            minWidth: '150px',
                            borderRadius: "8px",
                            border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                            background: isDarkMode ? "#2d3436" : "#ffffff",
                            color: isDarkMode ? "#ffffff" : "#000000",
                            padding: "8px 12px",
                            display: 'inline-block'
                          }}
                        >
                          <option value="Individual">Individual</option>
                          <option value="Boxes">Box</option>
                          <option value="Packages">Package</option>
                          <option value="Cartons">Carton</option>
                          <option value="Cases">Case</option>
                          <option value="Pallets">Pallet</option>
                          <option value="Bags">Bag</option>
                          <option value="Bottles">Bottle</option>
                          <option value="Cans">Can</option>
                        </select>
                        {inventoryState?.menuItem_PurchaseContainerType !== "Individual" && (
                          <>
                            <span style={{ color: isDarkMode ? '#ffffff' : '#000000', fontWeight: '500' }}>
                              of quantity:
                            </span>
                            <input
                              type="number"
                              min="1"
                              className="form-control"
                              value={inventoryState?.menuItem_PurchaseContainerQty || 1}
                              onChange={(e) => handleInputChange(e, "menuItem_PurchaseContainerQty")}
                              style={{
                                width: 'auto',
                                minWidth: '100px',
                                borderRadius: "8px",
                                border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                                background: isDarkMode ? "#2d3436" : "#ffffff",
                                color: isDarkMode ? "#ffffff" : "#000000",
                                padding: "8px 12px",
                                display: 'inline-block',
                              }}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}



            </PanelBody>
          </Panel>



          {/* Stock Entries Table - Only show when Track Stock is enabled */}
          {inventoryState?.menuItem_IsTrackStock && (
            <Panel className="mt-3">
              <PanelHeader
                style={{
                  backgroundColor: themeColor,
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px 10px 0 0',
                  padding: '12px 20px',
                  fontWeight: '600',
                  fontSize: '15px'
                }}
              >
                <i className="fa fa-table me-2"></i>
                Stock Entries
              </PanelHeader>
              <PanelBody
                style={{
                  padding: '16px 20px',
                  backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                  borderRadius: '0 0 10px 10px',
                  border: `1px solid ${themeColor}20`
                }}
              >
                {loadingStock ? (
                  <div className="text-center py-3">
                    <Preloader />
                  </div>
                ) : stockEntries.length === 0 ? (
                  <div className="text-center py-3" style={{ color: isDarkMode ? '#adb5bd' : '#6c757d', fontSize: '13px' }}>
                    <i className="fa fa-inbox me-2" style={{ fontSize: '20px' }}></i>
                    <p className="mb-0 mt-2">No stock entries found</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table
                      className="table table-hover"
                      style={{
                        backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                        color: isDarkMode ? '#ffffff' : '#000000',
                        fontSize: '13px',
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            backgroundColor: isDarkMode ? '#3c4043' : '#f8f9fa',
                            borderBottom: `2px solid ${themeColor}`,
                          }}
                        >
                          <th style={{ padding: '8px 12px', fontWeight: '600' }}>Batch Number</th>
                          <th style={{ padding: '8px 12px', fontWeight: '600' }}>Source Type</th>
                          <th style={{ padding: '8px 12px', fontWeight: '600' }}>Quantity</th>
                          <th style={{ padding: '8px 12px', fontWeight: '600' }}>Unit</th>
                          <th style={{ padding: '8px 12px', fontWeight: '600' }}>Container Type</th>
                          <th style={{ padding: '8px 12px', fontWeight: '600' }}>Container Count</th>
                          <th style={{ padding: '8px 12px', fontWeight: '600' }}>Cost Price</th>
                          <th style={{ padding: '8px 12px', fontWeight: '600' }}>Receipt Date</th>
                          <th style={{ padding: '8px 12px', fontWeight: '600' }}>Expiry Date</th>
                          <th style={{ padding: '8px 12px', fontWeight: '600' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockEntries.map((entry, index) => (
                          <tr
                            key={entry.inventoryStockId || index}
                            style={{
                              borderBottom: isDarkMode ? '1px solid #404040' : '1px solid #e9ecef',
                              transition: 'background-color 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = isDarkMode ? '#3c4043' : '#f8f9fa';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = isDarkMode ? '#2d3436' : '#ffffff';
                            }}
                          >
                            <td style={{ padding: '8px 12px' }}>
                              {entry.batchNumber || '-'}
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: getSourceTypeColor(entry.sourceType),
                                  color: '#ffffff',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                }}
                              >
                                {entry.sourceType || '-'}
                              </span>
                            </td>
                            <td style={{ padding: '8px 12px', fontWeight: '500' }}>
                              {entry.quantity?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              {entry.unit || '-'}
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              {entry.containerType ? (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: entry.containerType === 'Individual' ? '#6c757d' : '#17a2b8',
                                    color: '#ffffff',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                  }}
                                >
                                  {entry.containerType}
                                  {entry.containerType !== 'Individual' && (entry.containerQuantity ?? entry.ContainerQuantity) != null && (entry.containerQuantity ?? entry.ContainerQuantity) > 0 && (
                                    <> ({entry.containerQuantity ?? entry.ContainerQuantity} units)</>
                                  )}
                                </span>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td style={{ padding: '8px 12px', fontWeight: '500' }}>
                              {(() => {
                                const qtyNum = parseFloat(entry.quantity ?? entry.Quantity ?? 0) || 0;
                                const contQty = parseInt(entry.containerQuantity ?? entry.ContainerQuantity ?? 0, 10) || 0;
                                const numContainers = (entry.containerCount != null && entry.containerCount !== undefined)
                                  ? Number(entry.containerCount)
                                  : (contQty > 0 && entry.containerType && entry.containerType !== 'Individual')
                                    ? qtyNum / contQty
                                    : null;
                                if (numContainers == null || numContainers <= 0) return <span>-</span>;
                                return (
                                  <span>
                                    {numContainers.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                                    {entry.containerType && entry.containerType !== 'Individual' && (
                                      <span style={{ fontSize: '11px', color: isDarkMode ? '#adb5bd' : '#6c757d', marginLeft: '4px' }}>
                                        {entry.containerType.toLowerCase()}
                                      </span>
                                    )}
                                    {(!entry.containerType || entry.containerType === 'Individual') && (
                                      <span style={{ fontSize: '11px', color: isDarkMode ? '#adb5bd' : '#6c757d', marginLeft: '4px' }}>items</span>
                                    )}
                                  </span>
                                );
                              })()}
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              {entry.costPrice?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) || '0.00'}
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              {entry.receiptDate ? new Date(entry.receiptDate).toLocaleDateString() : '-'}
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              {entry.expiryDate ? (
                                <span
                                  style={{
                                    color: isExpired(entry.expiryDate) ? '#dc3545' : isDarkMode ? '#ffffff' : '#000000',
                                    fontWeight: isExpired(entry.expiryDate) ? '600' : 'normal',
                                  }}
                                >
                                  {new Date(entry.expiryDate).toLocaleDateString()}
                                  {isExpired(entry.expiryDate) && (
                                    <i className="fa fa-exclamation-triangle ms-2" style={{ fontSize: '12px' }}></i>
                                  )}
                                </span>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: entry.isActive ? '#28a745' : '#dc3545',
                                  color: '#ffffff',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                }}
                              >
                                {entry.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </PanelBody>
            </Panel>
          )}

          {/* Branch Stock Entries - Parent only (non-variant items) */}
          {isParentCompany && !hasVariants && inventoryState?.menuItem_IsTrackStock && hasBranches && (
            <Panel className="mt-3">
              <PanelHeader
                style={{
                  backgroundColor: themeColor,
                  color: "white",
                  border: "none",
                  borderRadius: "10px 10px 0 0",
                  padding: "12px 20px",
                  fontWeight: "600",
                  fontSize: "15px",
                }}
              >
                <i className="fa fa-sitemap me-2"></i>
                Branch Stock Entries
              </PanelHeader>
              <PanelBody
                style={{
                  padding: "16px 20px",
                  backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                  borderRadius: "0 0 10px 10px",
                  border: `1px solid ${themeColor}20`,
                }}
              >
                {loadingItemBranchTotals && (
                  <div className="text-center py-3">
                    <Preloader />
                  </div>
                )}

                {assignedBranches.length === 0 ? (
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    No assigned branches found for this item.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr
                          style={{
                            backgroundColor: isDarkMode ? "#3c4043" : "#f8f9fa",
                            borderBottom: `2px solid ${themeColor}`,
                          }}
                        >
                          <th>Branch</th>
                          <th style={{ width: 160 }}>Qty</th>
                          <th style={{ width: 160 }}>Lots</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignedBranches.map((b) => {
                          const bId = toInt(b.branchCompId);
                          const bs = itemBranchStockMap?.[bId] || {};
                          const expanded = !!bs.expanded;
                          const qty = bs.totalQty;
                          return (
                            <React.Fragment key={`item_${bId}`}>
                              <tr>
                                <td>{b.branchName}</td>
                                <td>
                                  {bs.loading
                                    ? "Loading..."
                                    : qty === null || qty === undefined
                                      ? "-"
                                      : toFloat(qty).toFixed(2)}
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => toggleItemBranchLots(bId)}
                                    style={{ borderRadius: 10 }}
                                  >
                                    {expanded ? "Hide lots" : "View lots"}
                                  </button>
                                </td>
                              </tr>
                              {expanded && (
                                <tr>
                                  <td colSpan={3}>
                                    {bs.loading ? (
                                      <div className="text-center py-3">
                                        <Preloader />
                                      </div>
                                    ) : (
                                      renderStockEntriesTable(bs.entries || [], { compact: true })
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </PanelBody>
            </Panel>
          )}

        </>
      )}

    </div>
  );
};

// Helper function to get color for source type
const getSourceTypeColor = (sourceType) => {
  const colors = {
    'PurchaseOrder': '#007bff',
    'RecipeBatch': '#28a745',
    'ManualAdjustment': '#ffc107',
    'Transfer': '#17a2b8',
  };
  return colors[sourceType] || '#6c757d';
};

// Helper function to check if expiry date is passed
const isExpired = (expiryDate) => {
  if (!expiryDate) return false;
  return new Date(expiryDate) < new Date();
};

export default UpdateItemInventory;
